import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import {
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    Avatar,
    Popover,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
} from "@mui/material";
import { useDispatch, useSelector } from "../../redux/store";
import { updateDashboardPersonalTask } from "../../redux/slices/dashboardPersonal";
import styles from "./TaskCard.module.css";
import buttonStyle from "../../GlobalStyles/button.module.css";

import FolderIcon from "@mui/icons-material/Folder"; // Assuming you have @mui/icons-material installed
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import folderIcon from "../../assets/folder.svg";
import calendarIcon from "../../assets/Calendar.svg";
import timeIcon from "../../assets/Time.svg";
import {
    completeTask,
    changeDeadline,
    changeAssignee,
    fetchProfilePictureCached,
    clearProfilePictureMemoryCache,
} from "../../redux/slices/dashboard";

import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"; // For the unchecked state
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; // For the checked state
import DatePicker from "../DatePicker/DatePicker";
import FeePropFeePopUp from "../FeePropFeePopUp/FeePropFeePopUp";

function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
}

function stringAvatar(nameOrInitials) {
    if (nameOrInitials && typeof nameOrInitials === 'string' && nameOrInitials.trim()) {
        const trimmed = nameOrInitials.trim();

        // If it's already initials (2 characters), use as-is
        if (trimmed.length === 2 && !trimmed.includes(' ')) {
            return {
                sx: {
                    bgcolor: stringToColor(trimmed),
                },
                children: trimmed.toUpperCase(),
            };
        }

        // Otherwise, extract initials from name
        const parts = trimmed.split(" ");
        const firstInitial = parts[0] ? parts[0][0] : '?';
        const secondInitial = parts[1] ? parts[1][0] : (parts[0] && parts[0].length > 1 ? parts[0][1] : '?');

        return {
            sx: {
                bgcolor: stringToColor(trimmed),
            },
            children: `${firstInitial}${secondInitial}`.toUpperCase(),
        };
    }
    return {
        sx: {
            bgcolor: '#ccc',
        },
        children: '??',
    };
}

const TaskCard = ({ task, setupUploadModal, personalMode = false, boardFullWidth = false, compact = false, truncateTitleOneLine = false, titleMaxWidth, largeAvatar = false }) => {
    const { accounts } = useMsal();
    const dispatch = useDispatch();
    const API_URL = process.env.REACT_APP_API_URL;

    const account = accounts[0];

    const isUploadType = task.deadlineType === "upload";
    // Normalize completed value (handles boolean, numeric and string values)
    const completedValue = task.completed === true || task.completed === 1 || task.completed === '1' || task.completed === 'true';
    const [isCompleted, setIsCompleted] = useState(completedValue);
    const [assigneesList, setAssigneesList] = useState([]);
    const [taskData, setTaskData] = useState({});
    const { assignees } = useSelector((state) => state.dashboard);
    const [selectedAssignee, setSelectedAssignee] = useState({});

    const [feeModal, setFeeModal] = useState(false);

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const fetchAndSetAssigneesWithImages = async (filteredAssignees) => {
        const updatedAssigneesWithImages = await Promise.all(filteredAssignees.map(async (assignee) => {
            try {
                if (assignee.profilePicUrl) {
                    const cachedFetch = dispatch(fetchProfilePictureCached(assignee.profilePicUrl));
                    const imageUrl = await cachedFetch;
                    return { ...assignee, profilePicUrl: imageUrl };
                }
                return assignee;
            } catch (error) {
                console.error(`Error fetching profile image for ${assignee.name}:`, error);
                return assignee; // Return original assignee if image fetch fails
            }
        }));
        setAssigneesList(updatedAssigneesWithImages);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    useEffect(() => {
        if (assignees && task) {
            const handlerTypeProperty = getHandlerTypeProperty(task.deadlineType);
            const filteredAssignees = assignees.filter(assignee => assignee[handlerTypeProperty]);
            fetchAndSetAssigneesWithImages(filteredAssignees);
            const currentAssignee = filteredAssignees.find(assignee => assignee.id === task.assigneeID);
            setSelectedAssignee(currentAssignee || {});
        }
    }, [assignees, task]);

    useEffect(() => {
        // Once assigneesList is updated with images, also update selectedAssignee if it's one of the assignees
        const updatedSelected = assigneesList.find(assignee => assignee.id === selectedAssignee.id);
        if (updatedSelected) {
            setSelectedAssignee(updatedSelected);
        }
    }, [assigneesList, selectedAssignee.id]);

    // NEW: Caching logic for task photos and assignees
    const [cachedAssignees, setCachedAssignees] = useState([]);
    const [cachedMainProfilePic, setCachedMainProfilePic] = useState(null);

    // Create stable string keys for dependencies to avoid re-running effect on every render
    const assigneeEmailsKey = useMemo(() => {
        if (!task.assignedTo || !Array.isArray(task.assignedTo)) return '';
        return task.assignedTo.map(a => a?.email || '').join(',');
    }, [task.assignedTo]);

    const mainPicUrl = task.taskProfilePicUrl || '';

    useEffect(() => {
        let isMounted = true;

        const getRelativeUrl = (url) => {
            if (!url) return null;
            if (url.startsWith(API_URL)) {
                return url.substring(API_URL.length);
            }
            return url;
        };

        const cacheImages = async () => {
            // 1. Cache Main Profile Pic
            if (mainPicUrl) {
                try {
                    const relUrl = getRelativeUrl(mainPicUrl);
                    const blobUrl = await dispatch(fetchProfilePictureCached(relUrl));
                    if (isMounted) setCachedMainProfilePic(blobUrl);
                } catch (err) {
                    console.error("Failed to cache main pic", err);
                }
            }

            // 2. Cache Stacked Assignees
            if (task.assignedTo && Array.isArray(task.assignedTo) && task.assignedTo.length > 0) {
                const results = await Promise.all(task.assignedTo.map(async (assignee) => {
                    if (assignee && assignee.email) {
                        try {
                            const relUrl = `/api/bid/opportunity/profile/pic/${encodeURIComponent(assignee.email)}`;
                            const blobUrl = await dispatch(fetchProfilePictureCached(relUrl));
                            return { ...assignee, cachedUrl: blobUrl };
                        } catch (err) {
                            return assignee;
                        }
                    }
                    return assignee;
                }));
                if (isMounted) setCachedAssignees(results);
            }
        };

        cacheImages();

        return () => { isMounted = false; };
    }, [mainPicUrl, assigneeEmailsKey, API_URL, dispatch]);


    const getHandlerTypeProperty = (deadlineType) => {
        switch (deadlineType) {
            case "fee_proposal": return 'feeProposalHandler';
            case "isa_todo": return 'isaToDoHandler';
            case "isa_to_sent": return 'isaToSendHandler';
            case "fbps": return 'fbpHandler';
            default: return null;
        }
    };

    // Function to handle date change from DatePicker
    const handleDatesChange = (selection) => {
        const { startDate, endDate, isFinalSelection } = selection;

        // Convert ISO to dd/mm/yyyy
        const toDmy = (iso) => {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return null;
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        };

        // In Personal mode, never call the Opportunities deadline API.
        if (personalMode) {
            if (!isFinalSelection) {
                // Wait for end date selection
                return;
            }
            dispatch(updateDashboardPersonalTask({
                taskId: task.id,
                startDate: toDmy(startDate),
                dueDate: toDmy(endDate)
            }))
                .unwrap()
                .then((resp) => {
                    if (resp && resp.success && typeof window !== 'undefined') {
                        const evt = new CustomEvent('personalTasks:refresh');
                        window.dispatchEvent(evt);
                    }
                })
                .catch(() => { });
            return;
        }

        // Default Opportunities flow
        const dateObject = new Date(startDate);
        const day = dateObject.getDate();
        const month = dateObject.getMonth() + 1;
        const year = dateObject.getFullYear();
        const formattedDate = `${day}-${month}-${year}`;
        dispatch(
            changeDeadline(
                task.projectNumber,
                task.deadlineID,
                task.deadlineType,
                formattedDate,
                account.username
            )
        );
    };

    const toggleCompletion = (deadlineID, jobNumber, deadlineType) => {
        if (isCompleted) return; // Do nothing if already completed

        const newCompletionStatus = true;
        setIsCompleted(newCompletionStatus);

        // Personal dashboard: use personal update API
        if (personalMode) {
            dispatch(updateDashboardPersonalTask({
                taskId: task.id,
                completed: true,
            }))
                .unwrap()
                .then((resp) => {
                    if (resp && resp.success && typeof window !== 'undefined') {
                        const evt = new CustomEvent('personalTasks:refresh');
                        window.dispatchEvent(evt);
                    }
                })
                .catch(() => { });
            return;
        }

        // Opportunities flow (non-personal)
        setTaskData({ jobNumber, deadlineID, deadlineType, updatedBy: account.username });
        if (newCompletionStatus && deadlineType === "fee_proposal") {
            setFeeModal(true);
        } else {
            dispatch(
                completeTask(jobNumber, deadlineID, deadlineType, account.username)
            );
        }
    };

    // Helper function to calculate the difference in days between two dates
    const getDaysDifference = (date1, date2) => {
        const timeDiff = date2 - date1;
        return timeDiff / (1000 * 3600 * 24);
    };

    // Function to format the deadlineDate to "dd/mm" format
    const formatDeadlineDate = (deadlineDate) => {
        if (!deadlineDate) return "";
        const parts = deadlineDate.split("/");
        // Assuming the format is "dd/mm/yyyy"
        if (parts.length === 3) {
            const [day, month] = parts;
            return `${day}/${month}`; // Return "dd/mm" format
        }
        return deadlineDate; // Return original string if format is unexpected
    };

    // Function to determine the class based on the deadline date
    const getDeadlineClass = (deadlineStr) => {
        if (!deadlineStr || typeof deadlineStr !== 'string') {
            return "dateDueAfter7"; // Default to normal color if no deadline
        }
        // Assuming deadlineStr is in "dd/mm" format and the year is the current year
        const parts = deadlineStr.split("/");
        if (parts.length !== 2) {
            return "dateDueAfter7"; // Default if not in expected format
        }
        const [day, month] = parts.map(Number);
        if (isNaN(day) || isNaN(month) || day < 1 || day > 31 || month < 1 || month > 12) {
            return "dateDueAfter7"; // Default if invalid date parts
        }
        const year = new Date().getFullYear();
        const deadline = new Date(year, month - 1, day); // Month is 0-indexed in JS Date
        const today = new Date();

        // Set the hours, minutes, seconds, and milliseconds to 0 for both dates
        today.setHours(0, 0, 0, 0);
        deadline.setHours(0, 0, 0, 0);

        const daysDifference = getDaysDifference(today, deadline);

        if (daysDifference < -2) return "dateOverdue";
        if (daysDifference <= 7) return "dateDueIn7";
        return "dateDueAfter7";
    };

    const deadlineClass = getDeadlineClass(task.deadlineDate);
    const estimatedHours = typeof task.estimatedHours === 'number' ? task.estimatedHours : null;

    const updateAssignee = (assignee) => {
        dispatch(
            changeAssignee(
                task.projectNumber,
                task.deadlineID,
                task.deadlineType,
                task.assigneeID,
                assignee.id,
                assignee.name,
                account.username
            )
        );
    };
    // Apply completed style when task is completed
    const cardClass = `${styles.Card} ${isCompleted ? styles.completedTask : ''} ${compact ? styles.compactCard : ''}`;
    const cardStyleOverride = boardFullWidth ? { maxWidth: 'unset', width: '100%' } : undefined;

    const displayAssignees = cachedAssignees.length > 0 ? cachedAssignees : (task.assignedTo || []);

    return (
        <Card className={cardClass} style={cardStyleOverride}>
            <CardContent className={`${styles.CardContent} ${compact ? styles.compactCardContent : ''}`}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    {!isUploadType && (
                        <IconButton
                            onClick={() =>
                                toggleCompletion(
                                    task.deadlineID,
                                    task.projectNumber,
                                    task.deadlineType
                                )
                            }
                            aria-label="complete task"
                            size="small"
                            style={{ alignSelf: "start", margin: "-6px 0px 0 -6px" }}
                        >
                            {isCompleted ? (
                                <CheckCircleIcon sx={{ color: '#0B8A00' }} fontSize="small" />
                            ) : (
                                <RadioButtonUncheckedIcon fontSize="small" />
                            )}
                        </IconButton>
                    )}
                    <Typography
                        gutterBottom
                        variant="h5"
                        component="div"
                        className={`${styles.cardTitle} ${compact ? styles.compactTitle : (truncateTitleOneLine ? styles.singleLine : '')}`}
                        style={{ flex: 1, maxWidth: typeof titleMaxWidth === 'number' ? titleMaxWidth : (truncateTitleOneLine ? 168 : undefined) }}
                        title={task.taskTitle || `${task.projectNumber} - ${task.address}`}
                    >
                        <a
                            href={`/Projects/ProjectProfile#${task.projectNumber}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            {task.taskTitle || `${task.projectNumber} - ${task.address}`}
                        </a>
                    </Typography>
                </div>
                {!isUploadType && (
                    <Typography
                        gutterBottom
                        variant="h5"
                        component="div"
                        className={`${styles.cardSubtext} ${styles.client} ${compact ? styles.compactSubtext : ''}`}
                        title={task.clientName || ""}
                    >
                        {(task.clientName && String(task.clientName).trim()) ? task.clientName : '\u00A0'}
                    </Typography>
                )}
                <div className={styles.cardButtons}>
                    {isUploadType ? (
                        // Render the upload type layout
                        task?.files.map((file) => (
                            <React.Fragment key={file.uploadID}>
                                <Button
                                    className={`${buttonStyle.Button} ${file.completed ? buttonStyle.Disabled : ""
                                        }`}
                                    onClick={() =>
                                        !file.completed && setupUploadModal(task, file)
                                    }
                                >
                                    {file.uploadType}
                                </Button>
                            </React.Fragment>
                        ))
                    ) : (
                        // Render the alternative layout
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%",
                            }}
                        >
                            {/* Left cluster: time icon + hours, then calendar icon + date */}
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {estimatedHours !== null && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <img src={timeIcon} alt="Time" style={{ width: 22.5, height: 22.5, transform: 'translateY(-3px)' }} />
                                        <Typography
                                            variant="body2"
                                            className={`${styles.cardSubtext} ${styles.hours}`}
                                            sx={{ color: '#0B8A00', fontSize: '12px', lineHeight: '18px' }}
                                        >
                                            {estimatedHours}h
                                        </Typography>
                                    </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <DatePicker
                                        initialDate={task.deadlineDate}
                                        initialStartDate={task.startDate}
                                        initialEndDate={task.deadlineDate}
                                        onDatesChange={handleDatesChange}
                                        onlyIcon={true}
                                        inputHeight="18px"
                                        borderThickness="1px"
                                        borderColor="#3C96D4"
                                        oppCard={true}
                                        showDateDisplay={false}
                                        keepOpenOnSelect={personalMode}
                                    />
                                    <Typography
                                        variant="body2"
                                        className={`${styles.cardSubtext} ${styles.deadline} ${styles[deadlineClass]}`}
                                        sx={{ fontSize: '12px', lineHeight: '18px' }}
                                    >
                                        {formatDeadlineDate(task.deadlineDate)}
                                    </Typography>
                                </div>
                            </div>

                            {/* All other elements stacked to the far right */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    flexGrow: 1,
                                }}
                            >
                                {/* Folder icon next to the text */}
                                {!personalMode && (
                                    <div style={{ marginRight: "6px" }}>
                                        <img
                                            className={styles.FolderIcon}
                                            style={{ cursor: 'pointer' }}
                                            src={folderIcon}
                                            alt="Folder"
                                            onClick={() => { if (task.sharepointFolderLink) window.open(task.sharepointFolderLink.trim() + '%2FBID', '_blank') }}
                                        />
                                    </div>
                                )}
                                {
                                    // Handle multiple assignees from the new API structure
                                    displayAssignees && Array.isArray(displayAssignees) && displayAssignees.length > 0 ? (
                                        <div style={{ display: 'flex', gap: '2px' }}>
                                            {displayAssignees.slice(0, 3).filter(assignee => assignee && typeof assignee === 'object').map((assignee, index) => (
                                                <Avatar
                                                    key={assignee.email || assignee.fullName || index}
                                                    src={assignee.cachedUrl || undefined}
                                                    {...stringAvatar(assignee.fullName || assignee.initials || assignee.email || 'Unknown')}
                                                    sx={{
                                                        width: largeAvatar ? "28px" : "16px",
                                                        height: largeAvatar ? "28px" : "16px",
                                                        fontSize: largeAvatar ? "12px" : "8px",
                                                        border: largeAvatar ? '2px solid white' : '1px solid white',
                                                        marginLeft: index > 0 ? (largeAvatar ? '-8px' : '-4px') : '0',
                                                    }}
                                                    title={assignee.fullName || assignee.email || 'Unknown User'}
                                                />
                                            ))}
                                            {displayAssignees.length > 3 && (
                                                <div
                                                    style={{
                                                        width: largeAvatar ? '28px' : '16px',
                                                        height: largeAvatar ? '28px' : '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#e0e0e0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: largeAvatar ? '11px' : '8px',
                                                        color: '#666',
                                                        border: largeAvatar ? '2px solid white' : '1px solid white',
                                                        marginLeft: largeAvatar ? '-8px' : '-4px',
                                                    }}
                                                    title={`+${displayAssignees.length - 3} more`}
                                                >
                                                    +{displayAssignees.length - 3}
                                                </div>
                                            )}
                                        </div>
                                    ) : (cachedMainProfilePic || task.taskProfilePicUrl) ? (
                                        // Fallback to single assignee profile picture
                                        <Avatar
                                            aria-describedby={id}
                                            src={cachedMainProfilePic || task.taskProfilePicUrl}
                                            sx={{
                                                width: largeAvatar ? "28px" : "18px",
                                                height: largeAvatar ? "28px" : "18px",
                                            }}
                                        />
                                    ) : selectedAssignee?.profilePicUrl ? (
                                        <Avatar
                                            aria-describedby={id}
                                            onClick={handleClick}
                                            src={selectedAssignee.profilePicUrl}
                                            sx={{
                                                width: largeAvatar ? "28px" : "18px",
                                                height: largeAvatar ? "28px" : "18px",
                                                cursor: "pointer",
                                            }}
                                        />
                                    ) : (
                                        <Avatar
                                            aria-describedby={id}
                                            onClick={handleClick}
                                            {...stringAvatar(selectedAssignee?.name)}
                                            sx={{
                                                width: largeAvatar ? "28px" : "18px",
                                                height: largeAvatar ? "28px" : "18px",
                                                fontSize: largeAvatar ? "12px" : "10px",
                                                cursor: "pointer",
                                            }}
                                        />
                                    )
                                }
                                <Popover
                                    id={id}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "right",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "left",
                                    }}
                                    sx={{
                                        ".MuiPaper-root": {
                                            borderRadius: "8px",
                                            border: "1px solid #3C96D4",
                                            boxShadow: "none",
                                            overflow: "hidden",
                                        },
                                    }}
                                >
                                    <List
                                        className={styles.AssigneeDropdown}
                                        sx={{
                                            width: "100%",
                                            maxWidth: 360,
                                            bgcolor: "background.paper",
                                        }}
                                    >
                                        {assigneesList.map((assignee) => (
                                            <ListItem
                                                key={assignee.id}

                                                sx={{ padding: "0px 8px 3px", cursor: "Pointer" }}
                                                onClick={() => updateAssignee(assignee)}
                                            >
                                                <ListItemAvatar sx={{ minWidth: "20px" }}>
                                                    {assignee.profilePicUrl ? (
                                                        <Avatar src={assignee.profilePicUrl}
                                                            sx={{
                                                                width: "17px",
                                                                height: "17px",
                                                                marginRight: "8px",
                                                            }}
                                                        />
                                                    ) : (
                                                        <Avatar
                                                            {...stringAvatar(assignee.name)}
                                                            sx={{
                                                                width: "15px",
                                                                height: "15px",
                                                                marginRight: "8px",
                                                            }}
                                                        />
                                                    )}
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={assignee.name}
                                                    primaryTypographyProps={{
                                                        variant: "body2",
                                                        style: {
                                                            color: "#292929",
                                                            textAlign: "left",
                                                            fontFamily: '"PT Sans", sans-serif',
                                                            fontSize: "13px",
                                                            fontStyle: "normal",
                                                            fontWeight: 700,
                                                            lineHeight: "normal",
                                                        },
                                                    }}
                                                    sx={{
                                                        margin: 0,
                                                        "&:hover": {
                                                            borderRadius: "6px",
                                                            background: "#EAF2F9",
                                                        },
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Popover>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
            <FeePropFeePopUp taskData={taskData} isVisible={feeModal} onClose={() => setFeeModal(false)} />

        </Card>
    );
};

export default TaskCard;
